import {
  Box,
  FormControlLabel,
  Grid,
  InputLabel,
  Switch,
  Typography,
  TextField,
  MenuItem,
  RadioGroup,
  Radio,
} from "@mui/material";
import { Field, Form, Formik } from "formik";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";

import AnimatedButton from "components/custom/AnimatedButton";
import { COUNTRY } from "config";
import { setLoaderText } from "store/features/loaderSlice";
import {
  setLoadingValue,
  setPurchase,
  setBindResponse,
} from "store/features/paymentSlice";
import { updatePurchaseAPI, bindQuote } from "api/siaApplication";
import {
  customStylesSelector,
  clientSelector,
  quotesSelector,
} from "store/selectors/selectors";
import { RootState } from "store/store";
import { useT } from "translation";
import PaymentFooter from "./PaymentFooter";
import { toggleAutoRenewalConsent } from "store/features/clientSlice";
import { nextStep, setShowPaymentScreen } from "store/features/stepperSlice";
import RenewalForm from "./RenewalForm";

export type CustomPaymentProps = {
  customerEmail?: string;
  onCancel: () => void;
  onSubmit: (details: any) => Promise<void>;
  paymentData?: PaymentPayload[];
  showAutoRenew?: boolean;
};

// Payment methods enum
enum PaymentMethod {
  CREDIT_CARD = "CREDIT_CARD",
  ACCOUNT_DEBIT = "ACCOUNT_DEBIT",
}

const CustomPayment: React.FC<CustomPaymentProps> = ({
  customerEmail,
  onSubmit,
  onCancel,
  paymentData,
  showAutoRenew = false,
}) => {
  const dispatch = useDispatch();
  const { t } = useT();
  const customStyles = useSelector(customStylesSelector);
  const [isLoading, setIsLoading] = useState(false);
  const [isPaymentFailed, setIsPaymentFailed] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(
    PaymentMethod.CREDIT_CARD
  );

  // Add selectors for client API key, purchase, and quotes
  const { clientAPIKey } = useSelector((state: RootState) => state.client);
  const { purchase } = useSelector((state: RootState) => state.payment);
  const { selectedQuotes } = useSelector((state: RootState) => state.quotes);

  const productSelected = selectedQuotes[0]?.product;

  // Add constants for dropdown optionswh
  const TIPO_LOGRADOURO_OPTIONS = [
    { value: "R.", label: "Rua" },
    { value: "AE", label: "Área Especial" },
    { value: "AL", label: "Alameda" },
    { value: "AV", label: "Avenida" },
    { value: "AR", label: "Área" },
    { value: "CH", label: "Chácara" },
    { value: "CJ", label: "Conjunto" },
    { value: "CL", label: "Colônia" },
    { value: "CM", label: "Caminho" },
    { value: "CO", label: "Condomínio" },
    { value: "DT", label: "Distrito" },
    { value: "EP", label: "Esplanada" },
    { value: "ES", label: "Estrada" },
    { value: "ET", label: "Estação" },
    { value: "FE", label: "Feira" },
    { value: "FV", label: "Favela" },
    { value: "FZ", label: "Fazenda" },
    { value: "JD", label: "Jardim" },
    { value: "LA", label: "Ladeira" },
    { value: "LD", label: "Lado" },
    { value: "LO", label: "Lote" },
    { value: "LG", label: "Largo" },
    { value: "LT", label: "Loteamento" },
    { value: "MO", label: "Módulo" },
    { value: "NU", label: "Núcleo" },
    { value: "OU", label: "Outros" },
    { value: "PC", label: "Praça" },
    { value: "PQ", label: "Parque" },
    { value: "PS", label: "Passagem" },
    { value: "PT", label: "Pátio" },
    { value: "QD", label: "Quadra" },
    { value: "RC", label: "Recreio" },
    { value: "RD", label: "Rodovia" },
    { value: "RS", label: "Residencial" },
    { value: "S.", label: "Setor" },
    { value: "ST", label: "Sítio" },
    { value: "TC", label: "Travessa Particular" },
    { value: "TV", label: "Travessa" },
    { value: "TO", label: "Trecho" },
    { value: "V.", label: "Via" },
    { value: "VD", label: "Viaduto" },
    { value: "VI", label: "Vila" },
    { value: "VL", label: "Vale" },
    { value: "VE", label: "Vereda" },
    { value: "VR", label: "Viela" },
  ];

  const TIPO_ENDERECO_OPTIONS = [
    { value: "RESIDENCIAL", label: "Residencial" },
    { value: "COMERCIAL", label: "Comercial" },
  ];

  const CONTA_TIPO_OPTIONS = [
    { value: "CONTA_CORRENTE", label: "Conta Corrente" },
    { value: "CONTA_POUPANCA", label: "Conta Poupança" },
  ];

  // Options for regular account debit parentesco field
  const PARENTESCO_OPTIONS = [
    { value: "1", label: "Titular" },
    { value: "2", label: "Companheiro(a)" },
    { value: "3", label: "Cônjuge" },
    { value: "4", label: "Irmã(o)" },
    { value: "5", label: "Filho(a)" },
    { value: "6", label: "Mãe" },
    { value: "7", label: "Pai" },
  ];

  // Options for homeowners account relationship field
  const ACCOUNT_RELATIONSHIP_OPTIONS = [
    { value: "0", label: "Titular" },
    { value: "2", label: "Companheiro(a)" },
    { value: "1", label: "Cônjuge" },
    { value: "7", label: "Irmão" },
    { value: "3", label: "Filho(a)" },
    { value: "5", label: "Mãe" },
    { value: "4", label: "Pai" },
  ];

  const BANK_OPTIONS = [
    { value: "0237", label: "0237 - BANCO BRADESCO S.A." },
    { value: "0033", label: "0033 - BANCO SANTANDER S.A." },
    { value: "0001", label: "0001 - BANCO DO BRASIL S.A." },
    { value: "0104", label: "0104 - CAIXA ECONOMICA FEDERAL" },
    { value: "0756", label: "0756 - BANCO COOPERATIVO DO BRASIL S.A." },
    { value: "0748", label: "0748 - BANCO COOPERATIVO SICREDI S.A. - BA" },
    { value: "0041", label: "0041 - BANCO DO ESTADO DO RIO GRANDE DO SU" },
    { value: "0004", label: "0004 - BANCO DO NORDESTE DO BRASIL S.A." },
    { value: "0047", label: "0047 - BANCO DO ESTADO DE SERGIPE S.A." },
    { value: "0070", label: "0070 - BRB - BANCO DE BRASILIA S.A." },
    { value: "0084", label: "0084 - SISPRIME DO BRASIL" },
    { value: "0085", label: "0085 - Banco Cecred" },
    { value: "0133", label: "0133 - CRESOL CONFEDERAÇÃO" },
    { value: "0260", label: "0260 - NU PAGAMENTOS S.A." },
    { value: "0341", label: "0341 - ITAU" },
    { value: "0422", label: "0422 - BANCO SAFRA S.A." },
    { value: "0991", label: "0991 - BANCO CREDICOAMO" },
    { value: "0992", label: "0992 - BANCO MONDIAL" },
  ];

  // Check if product is HOMEOWNERS-ALLIANZ to determine payment method availability
  const isHomeownersAllianz = productSelected === "HOMEOWNERS-ALLIANZ";

  const getInitialValues = () => ({
    // Payment method - default to debit if credit card is not available
    paymentMethod: isHomeownersAllianz
      ? PaymentMethod.ACCOUNT_DEBIT
      : PaymentMethod.CREDIT_CARD,

    // Credit card fields
    card_owner_name: "",
    card_owner_cpf_cnpj: "",
    card_brand: "VISA",
    card_number: "",
    card_exp_month: "",
    card_exp_year: "",
    card_sec_code: "",

    // Account debit fields
    nomeTitular: "",
    documentoTitular: "",
    parentesco: "1",
    bancoNumero: "756",
    agenciaNumero: "",
    contaTipo: "CONTA_CORRENTE",
    contaNumero: "",
    contaDigito: "",
    diaPagamento: "",

    // Additional fields for HOMEOWNERS-ALLIANZ
    ownerAccountBankRelationship: "",

    // Address fields (common to both payment methods)
    tipoEndereco: "RESIDENCIAL",
    tipoLogradouro: "R.",
    logradouro: "",
    numero: "",
    cep: "",
    bairro: "",

    // Additional renewal info
    additional_renewal_info: {
      ci_code: "",
      end_date: "",
      policy_number: "",
    },
  });

  const creditCardValidationSchema = Yup.object().shape({
    is_policyholder: Yup.boolean().required(
      "É obrigatório informar se o segurado é o titular do cartão"
    ),
    card_owner_name: Yup.string().required(
      "Nome do titular do cartão é obrigatório"
    ),
    card_owner_cpf_cnpj: Yup.string()
      .matches(
        /^\d{11}$|^\d{14}$/,
        "CPF deve conter 11 dígitos ou CNPJ deve conter 14 dígitos"
      )
      .required("CPF/CNPJ é obrigatório"),
    card_brand: Yup.string().required("Bandeira do cartão é obrigatória"),
    card_number: Yup.string()
      .matches(/^\d{16}$/, "Número do cartão deve conter 16 dígitos")
      .required("Número do cartão é obrigatório"),
    card_exp_month: Yup.string()
      .matches(/^(0[1-9]|1[0-2])$/, "Mês deve estar entre 01 e 12")
      .required("Mês de validade é obrigatório"),
    card_exp_year: Yup.string()
      .matches(/^\d{2}$/, "Ano deve conter 2 dígitos")
      .required("Ano de validade é obrigatório"),
    card_sec_code: Yup.string()
      .matches(/^\d{3,4}$/, "Código de segurança deve conter 3 ou 4 dígitos")
      .required("Código de segurança é obrigatório"),
    tipoEndereco: Yup.string().required("Tipo de endereço é obrigatório"),
    tipoLogradouro: Yup.string().required("Tipo de logradouro é obrigatório"),
    logradouro: Yup.string().required("Logradouro é obrigatório"),
    numero: Yup.string().required("Número é obrigatório"),
    cep: Yup.string()
      .matches(/^\d{8}$/, "CEP deve conter 8 dígitos")
      .required("CEP é obrigatório"),
    bairro: Yup.string().required("Bairro é obrigatório"),
    additional_renewal_info: Yup.object().when([], {
      is: () =>
        selectedQuotes &&
        selectedQuotes.length > 0 &&
        selectedQuotes[0]?.data?.allianzRenewal,
      then: () =>
        Yup.object().shape({
          ci_code: Yup.string().required(
            "Código de identificação é obrigatório"
          ),
          end_date: Yup.string().required(
            "Data de fim de vigência é obrigatória"
          ),
          policy_number: Yup.string().required(
            "Número da apólice é obrigatório"
          ),
        }),
      otherwise: () =>
        Yup.object().shape({
          ci_code: Yup.string(),
          end_date: Yup.string(),
          policy_number: Yup.string(),
        }),
    }),
  });

  const accountDebitValidationSchema = Yup.object().shape({
    nomeTitular: Yup.string().required("Nome do titular é obrigatório"),
    documentoTitular: Yup.string()
      .matches(/^\d{11}$/, "CPF deve conter 11 dígitos")
      .required("CPF é obrigatório"),
    parentesco: Yup.string().required("Parentesco é obrigatório"),
    bancoNumero: Yup.string().required("Número do banco é obrigatório"),
    agenciaNumero: Yup.string().required("Número da agência é obrigatório"),
    contaTipo: Yup.string().required("Tipo de conta é obrigatório"),
    contaNumero: Yup.string().required("Número da conta é obrigatório"),
    contaDigito: Yup.string().required("Dígito da conta é obrigatório"),
    diaPagamento: Yup.string()
      .matches(/^(0?[1-9]|[12][0-9]|3[01])$/, "Dia deve estar entre 1 e 31")
      .required("Dia do pagamento é obrigatório"),
    // Additional fields validation for HOMEOWNERS-ALLIANZ
    ...(isHomeownersAllianz && {
      ownerAccountBankRelationship: Yup.string().required(
        "Relacionamento com o titular da conta é obrigatório"
      ),
    }),
    tipoEndereco: Yup.string().required("Tipo de endereço é obrigatório"),
    tipoLogradouro: Yup.string().required("Tipo de logradouro é obrigatório"),
    logradouro: Yup.string().required("Logradouro é obrigatório"),
    numero: Yup.string().required("Número é obrigatório"),
    cep: Yup.string()
      .matches(/^\d{8}$/, "CEP deve conter 8 dígitos")
      .required("CEP é obrigatório"),
    bairro: Yup.string().required("Bairro é obrigatório"),
    additional_renewal_info: Yup.object().when([], {
      is: () =>
        selectedQuotes &&
        selectedQuotes.length > 0 &&
        selectedQuotes[0]?.data?.allianzRenewal,
      then: () =>
        Yup.object().shape({
          ci_code: Yup.string().required(
            "Código de identificação é obrigatório"
          ),
          end_date: Yup.string().required(
            "Data de fim de vigência é obrigatória"
          ),
          policy_number: Yup.string().required(
            "Número da apólice é obrigatório"
          ),
        }),
      otherwise: () =>
        Yup.object().shape({
          ci_code: Yup.string(),
          end_date: Yup.string(),
          policy_number: Yup.string(),
        }),
    }),
  });

  const validationSchema = Yup.lazy((values) => {
    return values.paymentMethod === PaymentMethod.CREDIT_CARD
      ? creditCardValidationSchema
      : accountDebitValidationSchema;
  });

  const _onSubmit = async (values: any) => {
    try {
      dispatch(setLoadingValue(true));
      dispatch(setLoaderText(t("PROCESSING_PAYMENT")));
      setIsLoading(true);

      let paymentDetails;

      if (values.paymentMethod === PaymentMethod.CREDIT_CARD) {
        // Process credit card payment
        const cardNumber = values.card_number;
        const cnbr = btoa(cardNumber); // Basic base64 encoding

        paymentDetails = {
          type: "credit",
          dadosTitular: {
            nomeTitular: values.card_owner_name,
            documentoTitular: values.card_owner_cpf_cnpj || "07532221679",
            parentesco: values.parentesco,
          },
          contaTipo: values.contaTipo,
          card_acquirer: "CIELO_ECOMMERCE",
          card_owner_name: values.card_owner_name,
          card_brand: values.card_brand,
          card_number: cnbr,
          card_exp_month: values.card_exp_month,
          card_exp_year: values.card_exp_year,
          card_sec_code: values.card_sec_code,
          bairro: values.bairro,
          cep: values.cep,
          logradouro: values.logradouro,
          numero: Number(values.numero),
          tipoEndereco: values.tipoEndereco,
          tipoLogradouro: values.tipoLogradouro,
        };
      } else {
        // Process account debit payment
        paymentDetails = {
          type: "debit",
          dadosTitular: {
            nomeTitular: values.nomeTitular,
            documentoTitular: values.documentoTitular,
            parentesco: values.parentesco,
          },
          bancoNumero: values.bancoNumero,
          agenciaNumero: values.agenciaNumero,
          contaTipo: values.contaTipo,
          contaNumero: values.contaNumero,
          contaDigito: values.contaDigito,
          diaPagamento: values.diaPagamento,
          bairro: values.bairro,
          cep: values.cep,
          logradouro: values.logradouro,
          numero: Number(values.numero),
          tipoEndereco: values.tipoEndereco,
          tipoLogradouro: values.tipoLogradouro,
          // Add additional fields for HOMEOWNERS-ALLIANZ
          ...(isHomeownersAllianz && {
            ownerAccountBankRelationship: values.ownerAccountBankRelationship,
          }),
        };
      }

      // Only add renewal info if the renewal form is being displayed
      if (
        selectedQuotes &&
        selectedQuotes.length > 0 &&
        selectedQuotes[0]?.data?.allianzRenewal
      ) {
        paymentDetails.additional_renewal_info = values.additional_renewal_info;
      }

      // Update the purchase with payment details
      await updatePurchaseAPI(clientAPIKey, purchase.id, {
        auto_renewal_consent: false,
        payment_collection_info: paymentDetails,
      });

      // Get the first selected quote and bind it
      if (selectedQuotes && selectedQuotes.length > 0) {
        const selectedQuote = selectedQuotes[0];
        const bindResponse: any = await bindQuote(
          clientAPIKey,
          selectedQuote.id
        );
        console.log("bindResponse", bindResponse);

        // Store the bind response in Redux
        dispatch(setBindResponse(bindResponse?.data));

        // Check if the quote binding was successful
        if (
          bindResponse &&
          bindResponse?.data &&
          bindResponse?.data.status === "bind-failed"
        ) {
          // If there are blocked alerts, still process as successful but save alerts for display
          if (bindResponse?.data?.response?.blockedAlerts) {
            console.log(
              "Processing with blocked alerts:",
              bindResponse?.data?.response?.blockedAlerts
            );
            setIsLoading(false);
            setIsPaymentFailed(false);
            const updatedPurchase = {
              ...purchase,
              status: "paid",
            };
            dispatch(setPurchase(updatedPurchase));
            dispatch(nextStep());
            dispatch(setShowPaymentScreen(false));
          } else {
            console.error(
              "Quote binding failed:",
              bindResponse?.data?.response?.errors
            );
            setIsPaymentFailed(true);
            return;
          }
        } else if (
          bindResponse &&
          bindResponse?.data &&
          bindResponse?.data.status === "bound"
        ) {
          setIsLoading(false);
          setIsPaymentFailed(false);
          const updatedPurchase = {
            ...purchase,
            status: "paid",
          };
          dispatch(setPurchase(updatedPurchase));
          dispatch(nextStep());
          dispatch(setShowPaymentScreen(false));
        }
      }
    } catch (error) {
      console.error("Payment Processing Error:", error);
      setIsPaymentFailed(true);
    } finally {
      setIsLoading(false);
      dispatch(setLoadingValue(false));
      dispatch(setLoaderText(null));
    }
  };

  const InputLabelStyle = {
    color: customStyles.input.color,
    fontSize: "14px",
    fontWeight: "500",
    mb: "10px",
    fontFamily: customStyles.muiTheme.typography.fontFamily,
  };

  const TextFieldStyle = {
    ".MuiOutlinedInput-notchedOutline": {
      border: `1px solid ${customStyles?.input?.borderColor ?? "#a8a8a8"}`,
      "&:hover": {
        border: `1px solid ${customStyles?.input?.hover?.borderColor}`,
      },
    },
    ".MuiInputBase-root": {
      height: "54px",
      borderRadius: "6px",
      fontWeight: "normal",
      color: customStyles.input.color,
      backgroundColor: customStyles.input.backgroundColor,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      border: `2px solid ${customStyles?.input?.active?.borderColor}`,
    },
  };

  // Card brands options
  const CARD_BRAND_OPTIONS = [
    { value: "VISA", label: "Visa" },
    { value: "MASTERCARD", label: "Mastercard" },
    { value: "AMEX", label: "American Express" },
    { value: "ELO", label: "Elo" },
    { value: "HIPERCARD", label: "Hipercard" },
  ];

  // Month options for credit card expiration
  const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => {
    const month = (i + 1).toString().padStart(2, "0");
    return { value: month, label: month };
  });

  // Year options for credit card expiration (current year + 20 years)
  const currentYear = new Date().getFullYear() % 100; // Get last 2 digits of year
  const YEAR_OPTIONS = Array.from({ length: 20 }, (_, i) => {
    const year = (currentYear + i).toString().padStart(2, "0");
    return { value: year, label: year };
  });

  if (isPaymentFailed) {
    return (
      <Box
        display="flex"
        justifyContent={"center"}
        alignItems={"center"}
        flexDirection={"column"}
        py={4}
      >
        <Typography color={"error"} variant="h6">
          {t("PAYMENT_PAGE_FAILURE_MESSAGE")}
        </Typography>
        <AnimatedButton
          variant="text"
          onClick={() => setIsPaymentFailed(false)}
        >
          <Typography
            sx={{
              fontSize: "14px",
              color: customStyles?.button?.backgroundColor,
            }}
          >
            {t("PAYMENT_PAGE_RETRY_PAYMENT")}
          </Typography>
        </AnimatedButton>
      </Box>
    );
  }

  return (
    <Formik
      initialValues={getInitialValues()}
      validateOnMount={true}
      validationSchema={validationSchema}
      onSubmit={_onSubmit}
    >
      {({ handleSubmit, errors, touched, values, setFieldValue }) => (
        <Form id="payment-form" onSubmit={handleSubmit}>
          {/* Renewal Information Section */}
          {selectedQuotes &&
            selectedQuotes.length > 0 &&
            selectedQuotes[0]?.data?.allianzRenewal &&
            productSelected !== "MOTO-ALLIANZ" && (
              <Box sx={{ marginBottom: "1.5rem" }}>
                <RenewalForm errors={errors} touched={touched} />
              </Box>
            )}

          {/* Payment Method Section */}
          <Box sx={{ marginBottom: "1.5rem" }}>
            <Typography variant="h6" gutterBottom>
              Forma de Pagamento
            </Typography>
            <Field name="paymentMethod">
              {({ field }) => (
                <RadioGroup
                  row
                  {...field}
                  onChange={(e) => {
                    const newMethod = e.target.value as PaymentMethod;
                    setFieldValue("paymentMethod", newMethod);
                    setPaymentMethod(newMethod);
                  }}
                >
                  <FormControlLabel
                    value={PaymentMethod.CREDIT_CARD}
                    control={<Radio />}
                    label="Cartão de Crédito"
                  />
                  <FormControlLabel
                    value={PaymentMethod.ACCOUNT_DEBIT}
                    control={<Radio />}
                    label="Débito em Conta"
                  />
                </RadioGroup>
              )}
            </Field>
          </Box>

          {/* Credit Card Section */}
          {values.paymentMethod === PaymentMethod.CREDIT_CARD ? (
            <Box sx={{ marginBottom: "1.5rem" }}>
              <Typography variant="h6" gutterBottom>
                Dados do Cartão
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <InputLabel sx={InputLabelStyle}>
                    O segurado é o titular do cartão?
                  </InputLabel>
                  <Field name="is_policyholder">
                    {({ field }) => (
                      <RadioGroup
                        row
                        {...field}
                        onChange={(e) => {
                          setFieldValue(
                            "is_policyholder",
                            e.target.value === "true"
                          );
                        }}
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          gap: 2,
                          marginTop: 1,
                        }}
                      >
                        <FormControlLabel
                          value="true"
                          control={<Radio size="small" />}
                          label="Sim"
                          sx={{
                            "& .MuiFormControlLabel-label": {
                              fontSize: "14px",
                              fontWeight: "normal",
                            },
                          }}
                        />
                        <FormControlLabel
                          value="false"
                          control={<Radio size="small" />}
                          label="Não"
                          sx={{
                            "& .MuiFormControlLabel-label": {
                              fontSize: "14px",
                              fontWeight: "normal",
                            },
                          }}
                        />
                      </RadioGroup>
                    )}
                  </Field>
                </Grid>
                <Grid item xs={12}>
                  <InputLabel sx={InputLabelStyle}>
                    Nome do titular do cartão
                  </InputLabel>
                  <Field
                    as={TextField}
                    fullWidth
                    name="card_owner_name"
                    sx={TextFieldStyle}
                    error={
                      touched.card_owner_name && Boolean(errors.card_owner_name)
                    }
                    helperText={
                      touched.card_owner_name && errors.card_owner_name
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <InputLabel sx={InputLabelStyle}>CPF/CNPJ</InputLabel>
                  <Field
                    as={TextField}
                    fullWidth
                    name="card_owner_cpf_cnpj"
                    sx={TextFieldStyle}
                    error={
                      touched.card_owner_cpf_cnpj &&
                      Boolean(errors.card_owner_cpf_cnpj)
                    }
                    helperText={
                      touched.card_owner_cpf_cnpj && errors.card_owner_cpf_cnpj
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InputLabel sx={InputLabelStyle}>
                    Bandeira do Cartão
                  </InputLabel>
                  <Field
                    as={TextField}
                    select
                    fullWidth
                    name="card_brand"
                    sx={TextFieldStyle}
                    error={touched.card_brand && Boolean(errors.card_brand)}
                    helperText={touched.card_brand && errors.card_brand}
                  >
                    {CARD_BRAND_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Field>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InputLabel sx={InputLabelStyle}>Número do Cartão</InputLabel>
                  <Field
                    as={TextField}
                    fullWidth
                    name="card_number"
                    sx={TextFieldStyle}
                    error={touched.card_number && Boolean(errors.card_number)}
                    helperText={touched.card_number && errors.card_number}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <InputLabel sx={InputLabelStyle}>Mês de Validade</InputLabel>
                  <Field
                    as={TextField}
                    select
                    fullWidth
                    name="card_exp_month"
                    sx={TextFieldStyle}
                    error={
                      touched.card_exp_month && Boolean(errors.card_exp_month)
                    }
                    helperText={touched.card_exp_month && errors.card_exp_month}
                  >
                    {MONTH_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Field>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <InputLabel sx={InputLabelStyle}>Ano de Validade</InputLabel>
                  <Field
                    as={TextField}
                    select
                    fullWidth
                    name="card_exp_year"
                    sx={TextFieldStyle}
                    error={
                      touched.card_exp_year && Boolean(errors.card_exp_year)
                    }
                    helperText={touched.card_exp_year && errors.card_exp_year}
                  >
                    {YEAR_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Field>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <InputLabel sx={InputLabelStyle}>
                    Código de Segurança
                  </InputLabel>
                  <Field
                    as={TextField}
                    fullWidth
                    name="card_sec_code"
                    sx={TextFieldStyle}
                    error={
                      touched.card_sec_code && Boolean(errors.card_sec_code)
                    }
                    helperText={touched.card_sec_code && errors.card_sec_code}
                  />
                </Grid>
              </Grid>
            </Box>
          ) : (
            <>
              <Box sx={{ marginBottom: "1.5rem" }}>
                <Typography variant="h6" gutterBottom>
                  Dados do Titular da Conta
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <InputLabel sx={InputLabelStyle}>
                      Nome do titular do cartão
                    </InputLabel>
                    <Field
                      as={TextField}
                      fullWidth
                      name="nomeTitular"
                      sx={TextFieldStyle}
                      error={touched.nomeTitular && Boolean(errors.nomeTitular)}
                      helperText={touched.nomeTitular && errors.nomeTitular}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InputLabel sx={InputLabelStyle}>CPF</InputLabel>
                    <Field
                      as={TextField}
                      fullWidth
                      name="documentoTitular"
                      sx={TextFieldStyle}
                      error={
                        touched.documentoTitular &&
                        Boolean(errors.documentoTitular)
                      }
                      helperText={
                        touched.documentoTitular && errors.documentoTitular
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InputLabel sx={InputLabelStyle}>
                      Grau de parentesco
                    </InputLabel>
                    <Field
                      as={TextField}
                      select
                      fullWidth
                      name="parentesco"
                      sx={TextFieldStyle}
                      error={touched.parentesco && Boolean(errors.parentesco)}
                      helperText={touched.parentesco && errors.parentesco}
                    >
                      {PARENTESCO_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Field>
                  </Grid>
                  {isHomeownersAllianz && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <InputLabel sx={InputLabelStyle}>
                          Relacionamento com Titular da Conta
                        </InputLabel>
                        <Field
                          as={TextField}
                          select
                          fullWidth
                          name="ownerAccountBankRelationship"
                          sx={TextFieldStyle}
                          error={
                            touched.ownerAccountBankRelationship &&
                            Boolean(errors.ownerAccountBankRelationship)
                          }
                          helperText={
                            touched.ownerAccountBankRelationship &&
                            errors.ownerAccountBankRelationship
                          }
                        >
                          {ACCOUNT_RELATIONSHIP_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Field>
                      </Grid>
                    </>
                  )}
                </Grid>
              </Box>

              <Box sx={{ marginBottom: "1.5rem" }}>
                <Typography variant="h6" gutterBottom>
                  Dados Bancários
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <InputLabel sx={InputLabelStyle}>
                      Número do Banco
                    </InputLabel>
                    <Field
                      as={TextField}
                      select={isHomeownersAllianz}
                      fullWidth
                      name="bancoNumero"
                      type={isHomeownersAllianz ? undefined : "number"}
                      sx={TextFieldStyle}
                      error={touched.bancoNumero && Boolean(errors.bancoNumero)}
                      helperText={touched.bancoNumero && errors.bancoNumero}
                    >
                      {isHomeownersAllianz &&
                        BANK_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                    </Field>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InputLabel sx={InputLabelStyle}>Agência</InputLabel>
                    <Field
                      as={TextField}
                      fullWidth
                      name="agenciaNumero"
                      type="number"
                      sx={TextFieldStyle}
                      error={
                        touched.agenciaNumero && Boolean(errors.agenciaNumero)
                      }
                      helperText={touched.agenciaNumero && errors.agenciaNumero}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InputLabel sx={InputLabelStyle}>Tipo de Conta</InputLabel>
                    <Field
                      as={TextField}
                      select
                      fullWidth
                      name="contaTipo"
                      sx={TextFieldStyle}
                      error={touched.contaTipo && Boolean(errors.contaTipo)}
                      helperText={touched.contaTipo && errors.contaTipo}
                    >
                      {CONTA_TIPO_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Field>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <InputLabel sx={InputLabelStyle}>
                      Número da Conta
                    </InputLabel>
                    <Field
                      as={TextField}
                      fullWidth
                      name="contaNumero"
                      sx={TextFieldStyle}
                      error={touched.contaNumero && Boolean(errors.contaNumero)}
                      helperText={touched.contaNumero && errors.contaNumero}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <InputLabel sx={InputLabelStyle}>Dígito</InputLabel>
                    <Field
                      as={TextField}
                      fullWidth
                      name="contaDigito"
                      sx={TextFieldStyle}
                      error={touched.contaDigito && Boolean(errors.contaDigito)}
                      helperText={touched.contaDigito && errors.contaDigito}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InputLabel sx={InputLabelStyle}>
                      Dia do Vencimento das Parcelas
                    </InputLabel>
                    <Field
                      as={TextField}
                      fullWidth
                      name="diaPagamento"
                      type="number"
                      inputProps={{ min: 1, max: 31 }}
                      sx={TextFieldStyle}
                      error={
                        touched.diaPagamento && Boolean(errors.diaPagamento)
                      }
                      helperText={touched.diaPagamento && errors.diaPagamento}
                    />
                  </Grid>
                </Grid>
              </Box>
            </>
          )}

          <Box sx={{ marginBottom: "1.5rem" }}>
            <Typography variant="h6" gutterBottom>
              Endereço
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <InputLabel sx={InputLabelStyle}>Tipo de Endereço</InputLabel>
                <Field
                  as={TextField}
                  select
                  fullWidth
                  name="tipoEndereco"
                  sx={TextFieldStyle}
                  error={touched.tipoEndereco && Boolean(errors.tipoEndereco)}
                  helperText={touched.tipoEndereco && errors.tipoEndereco}
                >
                  {TIPO_ENDERECO_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Field>
              </Grid>
              <Grid item xs={12} sm={6}>
                <InputLabel sx={InputLabelStyle}>Tipo de Logradouro</InputLabel>
                <Field
                  as={TextField}
                  select
                  fullWidth
                  name="tipoLogradouro"
                  sx={TextFieldStyle}
                  error={
                    touched.tipoLogradouro && Boolean(errors.tipoLogradouro)
                  }
                  helperText={touched.tipoLogradouro && errors.tipoLogradouro}
                >
                  {TIPO_LOGRADOURO_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Field>
              </Grid>
              <Grid item xs={12}>
                <InputLabel sx={InputLabelStyle}>Logradouro</InputLabel>
                <Field
                  as={TextField}
                  fullWidth
                  name="logradouro"
                  sx={TextFieldStyle}
                  error={touched.logradouro && Boolean(errors.logradouro)}
                  helperText={touched.logradouro && errors.logradouro}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <InputLabel sx={InputLabelStyle}>Número</InputLabel>
                <Field
                  as={TextField}
                  fullWidth
                  name="numero"
                  sx={TextFieldStyle}
                  error={touched.numero && Boolean(errors.numero)}
                  helperText={touched.numero && errors.numero}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <InputLabel sx={InputLabelStyle}>CEP</InputLabel>
                <Field
                  as={TextField}
                  fullWidth
                  name="cep"
                  sx={TextFieldStyle}
                  error={touched.cep && Boolean(errors.cep)}
                  helperText={touched.cep && errors.cep}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <InputLabel sx={InputLabelStyle}>Bairro</InputLabel>
                <Field
                  as={TextField}
                  fullWidth
                  name="bairro"
                  sx={TextFieldStyle}
                  error={touched.bairro && Boolean(errors.bairro)}
                  helperText={touched.bairro && errors.bairro}
                />
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ marginTop: "40px" }}>
            <PaymentFooter
              backButtonProps={{
                disabled: isLoading,
                onClickCB: onCancel,
                btnText: t("BACK"),
              }}
              submitButtonProps={{
                disabled: isLoading,
                onClickCB: handleSubmit,
                btnText: t("PAYMENT_PAGE_MAKE_PAYMENT"),
              }}
              isLoading={isLoading}
            />
          </Box>
        </Form>
      )}
    </Formik>
  );
};

export default CustomPayment;
